import { useEffect, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  FaGraduationCap,
  FaUniversity,
  FaSchool,
  FaRocket,
  FaCode,
  FaAward,
  FaMicrochip,
  FaTools
} from "react-icons/fa";
import { trackEvent } from "../utils/analytics";

const TimelinePage = () => {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"]
  });

  useEffect(() => {
    trackEvent("timeline_page_view", {
      event_category: "Navigation",
      event_label: "Visited Timeline Page",
    });
  }, []);

  const timelineData = [
    {
      date: "October 2025",
      title: "Software Developer Intern",
      organization: "Uganda Christian University",
      type: "work",
      icon: <FaCode />,
      color: "from-blue-500 to-indigo-600",
      description: "Currently working as a Software Engineer in the Management Information Systems department, developing and maintaining university systems.",
      skills: ["Full-Stack Development", "System Architecture", "Database Management"],
      status: "current"
    },
    {
      date: "July 2025 - October 2025",
      title: "AI Engineer",
      organization: "Zentrix AI Innovative Labs",
      type: "work",
      icon: <FaRocket />,
      color: "from-purple-500 to-pink-600",
      description: "Worked on cutting-edge AI solutions and machine learning projects, developing intelligent systems and deploying AI models.",
      skills: ["Machine Learning", "AI Development", "Model Deployment"],
      status: "completed"
    },
    {
      date: "September 2024 - June 2025",
      title: "Embedded Systems Engineer",
      organization: "AgroHarvest Innovations Limited",
      type: "work",
      icon: <FaMicrochip />,
      color: "from-green-500 to-emerald-600",
      description: "Developed embedded IoT solutions for precision agriculture by integrating environmental sensors, WiFi modules, and Arduino-based microcontrollers to enable real-time crop and soil monitoring.",
      skills: ["IoT", "Embedded C", "Sensors", "Arduino"],
      status: "completed"
    },
    {
      date: "2024",
      title: "Internship",
      organization: "Uganda Industrial Research Institute",
      type: "work",
      icon: <FaTools />,
      color: "from-orange-500 to-red-500",
      description: "Built a Home Security and Environment Monitoring System. Collaborated to build projects and integrated remote monitoring capabilities enabling Remote Monitoring of the Home.",
      skills: ["System Design", "Remote Monitoring", "Collaboration"],
      status: "completed"
    },
    {
      date: "July 2025",
      title: "BSc Computer Science - First Class Honours",
      organization: "Uganda Christian University",
      type: "education",
      icon: <FaGraduationCap />,
      color: "from-green-500 to-teal-600",
      description: "Graduated with First Class Honours in Computer Science. Specialized in AI, deep learning, full-stack development, and robotics. Completed multiple real-world projects including flood prediction systems and intelligent applications.",
      skills: ["AI & ML", "Full-Stack Dev", "Robotics", "Cloud Computing"],
      status: "completed",
      achievement: true
    },
    {
      date: "2021 - 2025",
      title: "Bachelor's Degree Programme",
      organization: "Uganda Christian University",
      type: "education",
      icon: <FaUniversity />,
      color: "from-amber-500 to-orange-600",
      description: "Four years of intensive study in Computer Science, focusing on software engineering, artificial intelligence, and practical application development.",
      skills: ["Software Engineering", "Research", "Team Collaboration"],
      status: "completed"
    },
    {
      date: "April 2021",
      title: "Uganda Advanced Certificate of Education (UACE)",
      organization: "High School",
      type: "education",
      icon: <FaSchool />,
      color: "from-cyan-500 to-blue-500",
      description: "Completed A-Level education, building foundation in sciences and mathematics.",
      skills: ["Mathematics", "Sciences", "Critical Thinking"],
      status: "completed"
    },
    {
      date: "October 2018",
      title: "Uganda Certificate of Education (UCE)",
      organization: "Secondary School",
      type: "education",
      icon: <FaSchool />,
      color: "from-rose-500 to-red-500",
      description: "Completed O-Level education with strong performance in sciences and mathematics.",
      skills: ["Core Sciences", "Mathematics", "Foundation Studies"],
      status: "completed"
    }
  ];

  return (
    <div ref={containerRef} className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 transition-colors duration-300">
      {/* Hero Section */}
      <motion.section
        className="relative min-h-[60vh] flex flex-col justify-center items-center text-center px-6 py-20"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        <div className="absolute inset-0 -z-10 bg-gradient-to-br from-blue-100 to-purple-100 dark:from-blue-900/20 dark:to-purple-900/20 opacity-50 blur-3xl" />

        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mb-6"
        >
          <FaAward className="text-6xl md:text-8xl text-blue-600 dark:text-blue-400 mx-auto mb-4" />
        </motion.div>

        <motion.h1
          className="text-4xl md:text-6xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 dark:from-blue-400 dark:to-purple-400 bg-clip-text text-transparent mb-6"
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          My Journey
        </motion.h1>

        <motion.p
          className="text-lg md:text-xl text-gray-700 dark:text-gray-300 max-w-3xl mx-auto leading-relaxed"
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          From student to First Class graduate to professional software engineer —
          explore the milestones that shaped my career in technology and innovation.
        </motion.p>

        <motion.div
          className="mt-8 animate-bounce text-blue-500 dark:text-blue-400 text-4xl"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
        >
          ↓
        </motion.div>
      </motion.section>

      {/* Timeline Section with Parallax */}
      <section className="max-w-6xl mx-auto px-6 py-16 relative">
        {/* Vertical Line with Progress */}
        <motion.div
          className="absolute left-1/2 transform -translate-x-1/2 w-1 bg-gradient-to-b from-blue-300 to-purple-300 dark:from-blue-600 dark:to-purple-600 hidden md:block"
          style={{
            height: "100%",
            scaleY: useTransform(scrollYProgress, [0, 1], [0, 1]),
            transformOrigin: "top"
          }}
        />

        <div className="space-y-24 md:space-y-32">
          {timelineData.map((item, index) => (
            <TimelineItem
              key={index}
              item={item}
              index={index}
              scrollProgress={scrollYProgress}
              totalItems={timelineData.length}
            />
          ))}
        </div>
      </section>

      {/* Bottom CTA */}
      <motion.section
        className="py-20 text-center px-6"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
      >
        <h2 className="text-3xl md:text-4xl font-bold text-gray-800 dark:text-gray-100 mb-6">
          The Journey Continues...
        </h2>
        <p className="text-lg text-gray-600 dark:text-gray-400 mb-8 max-w-2xl mx-auto">
          This is just the beginning. More achievements, experiences, and milestones to come.
        </p>
        <motion.a
          href="/contact"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="inline-block bg-gradient-to-r from-blue-600 to-purple-600 dark:from-blue-500 dark:to-purple-500 text-white px-8 py-4 rounded-full font-semibold shadow-lg hover:shadow-xl transition-shadow"
        >
          Let's Connect
        </motion.a>
      </motion.section>
    </div>
  );
};

// Timeline Item Component with Parallax
const TimelineItem = ({ item, index, scrollProgress, totalItems }) => {
  const isLeft = index % 2 === 0;
  const itemRef = useRef(null);

  // Parallax effect for each item
  const y = useTransform(
    scrollProgress,
    [index / 10, (index + 1) / 10],
    [100, -100]
  );

  return (
    <motion.div
      ref={itemRef}
      className={`relative flex items-center ${isLeft ? 'md:flex-row' : 'md:flex-row-reverse'} flex-col gap-8`}
      initial={{ opacity: 0, x: isLeft ? -100 : 100 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.8, delay: 0.2 }}
    >
      {/* Content Card */}
      <motion.div
        className="w-full md:w-5/12 relative z-10"
        style={{ y: y }}
      >
        <motion.div
          className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl p-6 md:p-8 relative overflow-hidden group hover:shadow-2xl transition-all duration-300 touch-manipulation"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          {/* Gradient Background */}
          <div className={`absolute inset-0 bg-gradient-to-br ${item.color} opacity-0 group-hover:opacity-5 dark:group-hover:opacity-10 transition-opacity duration-300`} />

          {/* Status Badge */}
          {item.status === "current" && (
            <div className="absolute top-4 right-4">
              <span className="bg-green-500 text-white text-xs px-3 py-1 rounded-full font-semibold animate-pulse">
                Current
              </span>
            </div>
          )}

          {item.achievement && (
            <div className="absolute top-4 right-4">
              <FaAward className="text-yellow-500 dark:text-yellow-400 text-2xl" />
            </div>
          )}

          {/* Date */}
          <div className="text-sm font-semibold text-gray-500 dark:text-gray-400 mb-3">
            {item.date}
          </div>

          {/* Title */}
          <h3 className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-gray-100 mb-2">
            {item.title}
          </h3>

          {/* Organization */}
          <p className="text-lg text-blue-600 dark:text-blue-400 font-semibold mb-4">
            {item.organization}
          </p>

          {/* Description */}
          <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-6">
            {item.description}
          </p>

          {/* Skills */}
          <div className="flex flex-wrap gap-2">
            {item.skills.map((skill, idx) => (
              <span
                key={idx}
                className={`text-xs px-3 py-1 rounded-full bg-gradient-to-r ${item.color} text-white font-medium`}
              >
                {skill}
              </span>
            ))}
          </div>
        </motion.div>
      </motion.div>

      {/* Center Icon */}
      <div className="relative z-20 flex-shrink-0">
        <motion.div
          className={`w-16 h-16 md:w-20 md:h-20 rounded-full bg-gradient-to-br ${item.color} flex items-center justify-center text-white text-2xl md:text-3xl shadow-lg touch-manipulation active:scale-95`}
          whileHover={{ scale: 1.2, rotate: 360 }}
          whileTap={{ scale: 0.9 }}
          transition={{ duration: 0.5 }}
        >
          {item.icon}
        </motion.div>

        {/* Connecting dots for mobile - make it smoother and behind everything */}
        {index < totalItems - 1 && (
          <div className="md:hidden absolute top-full left-1/2 transform -translate-x-1/2 w-1 h-24 bg-gradient-to-b from-blue-300 to-purple-300 dark:from-blue-600 dark:to-purple-600 -z-10" />
        )}
      </div>

      {/* Empty space on the other side for desktop */}
      <div className="w-full md:w-5/12 hidden md:block" />
    </motion.div>
  );
};

export default TimelinePage;

